import { Breadcrumbs, CircularProgress, Link, Tab, Tabs, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router'
import { type SyntheticEvent, useEffect, useState } from 'react'
import { GetOrganization } from '../../../services/OrganizationService'
import { type OrganizationDetailsModel } from '../../../models/OrganizationDetailsModel'
import { type AuthUser, useAuth } from '../../../hooks/use-auth'
import { TabDisplay } from '@oaisd/michdev.components.react'
import { CETADataTools } from './CETADataTools'
import { AuthFunctions, hasPermission } from '../../../utils/permissions'
import { AppRoutes } from '../../Routes'
import { NotAllowed } from '../../masterComponents/ProtectedRoute'
import { ArrowBack } from '@mui/icons-material'
import { SecurityLevel } from '../../../utils/genericTypes'
import { ImplementationAnnualPlanList } from './ImplementationAnnualPlanList'

export enum RCNImplementationTabs {
  ImplementationByYear = 0,
  DataTools = 1
}

export const ImplementationInstance = (): JSX.Element => {
  const { rcnId } = useParams()
  const [rcn, setRcn] = useState<OrganizationDetailsModel | null>(null)
  const { user } = useAuth()
  const [selectedTab, setSelectedTab] = useState<number>(RCNImplementationTabs.ImplementationByYear)
  const nav = useNavigate()

  const canAccessDataTools = user != null && hasPermission(user, AuthFunctions.CETA_PARTICIPATION)
  const canAccessImplementationByYear = user != null &&
    (hasPermission(user, AuthFunctions.ANNUAL_PLANNING_REVIEW) ||
    hasPermission(user, AuthFunctions.ANNUAL_PLANNING_REVIEW_EDIT) ||
    hasPermission(user, AuthFunctions.ANNUAL_PLANNING_ADMINISTRATION) ||
    hasPermission(user, AuthFunctions.ANNUAL_PLANNING_ADMINISTRATION_EDIT) ||
    hasPermission(user, AuthFunctions.ANNUAL_PLANNING_CONTRIBUTION) ||
    hasPermission(user, AuthFunctions.ANNUAL_PLANNING_CONTRIBUTION_EDIT))

  const canAccessRcnSelection = (user: AuthUser): boolean => [
    SecurityLevel.ProjectAdmin,
    SecurityLevel.ProjectUser
  ].includes(user.securityLevel)

  const canAccessTab = (tab: RCNImplementationTabs): boolean => {
    switch (tab) {
      case RCNImplementationTabs.ImplementationByYear: return canAccessImplementationByYear
      case RCNImplementationTabs.DataTools: return canAccessDataTools
      default: return false
    }
  }

  const navigateToTabWithAccess = (tab?: string): void => {
    if (tab != null) {
      const tabIndex = Object.values(RCNImplementationTabs).indexOf(Number(tab))
      if (tabIndex !== -1 && canAccessTab(tabIndex)) {
        setSelectedTab(tabIndex)
        return
      }
    }

    const tabs = Object.values(RCNImplementationTabs) as RCNImplementationTabs[]
    const accessibleTabs = tabs.filter(tab => canAccessTab(tab))
    if (accessibleTabs.length > 0) {
      const firstAccessibleTab = accessibleTabs[0]
      updateTab(firstAccessibleTab)
    }
  }

  const updateTab = (tab: RCNImplementationTabs): void => {
    setSelectedTab(tab)
  }

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (rcnId == null) {
        return
      }

      const rcn = await GetOrganization(rcnId)
      setRcn(rcn)
    }

    if (user == null) return

    void fetchData()
    navigateToTabWithAccess()
  }, [rcnId])

  if (rcn == null || user == null) {
    return <CircularProgress />
  }

  const handleTabChange = (e: SyntheticEvent, tab: RCNImplementationTabs): void => {
    updateTab(tab)
  }

  const goToRcnSelection = (): void => {
    nav(AppRoutes.IMPLEMENTATION)
  }

  if (!canAccessImplementationByYear && !canAccessDataTools) return <NotAllowed />

  return <>
    {canAccessRcnSelection(user) && (
      <Breadcrumbs maxItems={1} aria-label="breadcrumb" sx={{ mb: '.5em' }}>
        <Link sx={{ cursor: 'pointer' }} underline="hover" color="inherit" onClick={goToRcnSelection}>
          <ArrowBack sx={{ verticalAlign: 'middle', mr: '5px' }} />
          RCNs
        </Link>
      </Breadcrumbs>
    )}

    <Typography variant='h1' data-testid='pageHeader' mb='12px'>Implementation</Typography>
    <Typography variant='h4'>{rcn.name}</Typography>

    <Tabs sx={{ mb: '2em', mt: '1em' }} value={selectedTab} onChange={handleTabChange}>
      <Tab
        data-testid='implementationByYearTab'
        label='Implementation By Year'
        disabled={!canAccessImplementationByYear}
        value={RCNImplementationTabs.ImplementationByYear}
        sx={{ textTransform: 'none' }}
      />
      <Tab
        data-testid='dataToolsTab'
        label='Data Tools'
        disabled={!canAccessDataTools}
        value={RCNImplementationTabs.DataTools}
        sx={{ textTransform: 'none' }}
      />
    </Tabs>

    <TabDisplay index={RCNImplementationTabs.ImplementationByYear} value={selectedTab}>
      {canAccessImplementationByYear && (
        <ImplementationAnnualPlanList rcnId={rcn.id} />
      )}
    </TabDisplay>
    <TabDisplay index={RCNImplementationTabs.DataTools} value={selectedTab}>
      <Typography variant='h5'>Data Tools</Typography>
      {canAccessDataTools && <CETADataTools />}
    </TabDisplay>
  </>
}
